// Pure role + ownership authorization logic for Huntrack.
// Used by the frontend now; the same rules are mirrored in Supabase RLS (backend)
// once enabled. No I/O here so it is unit-testable.
//
// Roles: 'superadmin' (global) and 'agent' (own records only).
// Backward compat: the legacy profile role 'manager' is treated as 'superadmin'.

export const ROLES = { SUPERADMIN: 'superadmin', AGENT: 'agent' }

// Normalize a profile ({ name, role }) into a stable user context.
export function toUser(profile) {
  if (!profile) return null
  const role = profile.role === 'manager' || profile.role === 'superadmin' ? ROLES.SUPERADMIN : ROLES.AGENT
  return { name: profile.name || '', role }
}

export function isSuperadmin(user) {
  return !!user && user.role === ROLES.SUPERADMIN
}

// The owner field on a record (records key ownership on the agent display name).
function ownerOf(record) {
  return record?.owner_agent ?? record?.agent ?? null
}

// Can this user view a single record?
export function canViewRecord(user, record) {
  if (!user) return false
  if (isSuperadmin(user)) return true
  return ownerOf(record) === user.name
}

// Can this user edit a single record?
export function canEditRecord(user, record) {
  if (!user) return false
  if (isSuperadmin(user)) return true
  return ownerOf(record) === user.name
}

// Scope a list of records to what the user may see.
export function scopeRecords(user, records = []) {
  if (!user) return []
  if (isSuperadmin(user)) return records
  return records.filter(r => ownerOf(r) === user.name)
}

// Only superadmin may transfer ownership or change protected tags.
export function canTransferOwnership(user) {
  return isSuperadmin(user)
}
export function canManageTags(user) {
  return isSuperadmin(user)
}

// Agents see only their own personal records (diary/to-do); superadmin sees all.
export function scopePersonal(user, records = [], ownerField = 'agent') {
  if (!user) return []
  if (isSuperadmin(user)) return records
  return records.filter(r => (r[ownerField]) === user.name)
}
